import { useEffect, useMemo, useState, useCallback } from "react";
import { Navbar } from "@/components/Navbar";
import { SettingsDialog } from "@/components/SettingsDialog";
import { OrderFormDialog } from "@/components/OrderFormDialog";
import { OrderDetailsDialog } from "@/components/OrderDetailsDialog";
import { MetricDetailDialog } from "@/components/MetricDetailDialog";
import { ChangePasswordDialog } from "@/components/ChangePasswordDialog";
import { MetricCard } from "@/components/MetricCard";
import { ZonesTile, ZonesDetailDialog } from "@/components/ZonesTile";
import { ManageZonesDialog } from "@/components/ManageZonesDialog";
import { useZones } from "@/contexts/ZonesContext";
import { Button } from "@/components/ui/button";
import { Package, Layers, Wheat, Timer, Plus, ChevronRight, Download } from "lucide-react";
import { API_BASE, getToken } from "@/lib/api";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import api from "@/lib/api";
import useTicker from "@/hooks/useTicker";
import { computeOrder, formatNum, grainTypeMeta } from "@/lib/growthLogic";
import { toast } from "sonner";

export default function Dashboard() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [changePwOpen, setChangePwOpen] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [detailsOrder, setDetailsOrder] = useState(null);
  const [editingOrder, setEditingOrder] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [roundTrays, setRoundTrays] = useState(true);
  const [metricKind, setMetricKind] = useState(null);
  const [zonesOpen, setZonesOpen] = useState(false);
  const [manageZonesOpen, setManageZonesOpen] = useState(false);
  const { zones } = useZones();

  const ROW_LIMIT = 10;

  const now = useTicker(60_000);

  const fetchOrders = useCallback(async () => {
    try {
      const { data } = await api.get("/orders", { params: { delivered: false } });
      setOrders(data);
    } catch (err) {
      console.error("Failed to load orders:", err);
      if (err.response?.status !== 401) toast.error("Failed to load orders");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const computed = useMemo(
    () => orders.map((o) => ({ order: o, calc: computeOrder(o, now) })),
    [orders, now],
  );

  const metrics = useMemo(() => {
    const active = computed.length;
    const trays = computed.reduce(
      (acc, { calc }) => acc + (roundTrays ? calc.traysNeededRounded : calc.traysNeededRaw),
      0,
    );
    const grain = computed.reduce((acc, { calc }) => acc + calc.grainNeeded, 0);
    const harvestSoon = computed.filter(
      ({ calc }) => calc.hoursLeft <= 24 && calc.hoursLeft > 0,
    ).length;
    const overdue = computed.filter(({ calc }) => calc.overdue).length;
    return { active, trays, grain, harvestSoon, overdue };
  }, [computed, roundTrays]);

  const handleDeliver = async (order) => {
    try {
      await api.post(`/orders/${order.id}/deliver`);
      toast.success(`${order.customer_name} marked as delivered`);
      fetchOrders();
    } catch {
      toast.error("Failed to mark as delivered");
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/orders/${deleteTarget.id}`);
      toast.success("Order deleted");
      setDeleteTarget(null);
      fetchOrders();
    } catch {
      toast.error("Failed to delete order");
    }
  };

  const openDetails = (order) => setDetailsOrder(order);
  const openEdit = (order) => {
    setEditingOrder(order);
    setFormOpen(true);
  };

  const handleExport = async () => {
    try {
      const token = getToken();
      const res = await fetch(`${API_BASE}/orders/export.xlsx`, {
        method: "GET",
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const today = new Date().toISOString().slice(0, 10);
      a.download = `hydrostack-export-${today}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Export downloaded");
    } catch (err) {
      console.error(err);
      toast.error("Failed to export data");
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F7F5]">
      <Navbar
        onOpenSettings={() => setSettingsOpen(true)}
        onOpenChangePassword={() => setChangePwOpen(true)}
      />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8 fade-in-up">
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-emerald-700 mb-2">
              Production overview
            </div>
            <h1 className="font-display text-3xl sm:text-4xl font-medium tracking-tight text-stone-900">
              Dashboard
            </h1>
            <p className="text-sm text-stone-500 mt-1">
              Real-time tracking across all active orders.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setRoundTrays((r) => !r)}
              data-testid="toggle-round-trays"
              className="text-xs border-[#E5E5E0] bg-white"
            >
              Trays: {roundTrays ? "Rounded" : "Decimal"}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleExport}
              data-testid="export-xlsx-btn"
              className="gap-2 border-emerald-200 bg-white hover:bg-emerald-50 text-emerald-800"
            >
              <Download size={15} strokeWidth={2} />
              Export to Excel
            </Button>
            <Button
              onClick={() => {
                setEditingOrder(null);
                setFormOpen(true);
              }}
              data-testid="add-order-btn"
              className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2 shadow-sm"
            >
              <Plus size={16} strokeWidth={2.25} />
              Add New Order
            </Button>
          </div>
        </div>

        {/* Metrics — click for full live drill-down */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-10">
          <MetricCard
            testId="metric-active-orders"
            icon={Package}
            label="Active Orders"
            value={metrics.active}
            hint={metrics.overdue > 0 ? `${metrics.overdue} overdue` : "All on schedule"}
            theme="emerald"
            onClick={() => setMetricKind("active")}
          />
          <MetricCard
            testId="metric-total-trays"
            icon={Layers}
            label="Trays In Use"
            value={formatNum(metrics.trays, roundTrays ? 0 : 2)}
            hint={roundTrays ? "Rounded up per order" : "Exact decimal"}
            theme="teal"
            onClick={() => setMetricKind("trays")}
          />
          <MetricCard
            testId="metric-total-grain"
            icon={Wheat}
            label="Grain Required (kg)"
            value={formatNum(metrics.grain, 2)}
            hint="Across active batches"
            theme="amber"
            onClick={() => setMetricKind("grain")}
          />
          <MetricCard
            testId="metric-harvest-soon"
            icon={Timer}
            label="Harvest ≤ 24h"
            value={metrics.harvestSoon}
            hint="Orders ready within a day"
            theme="rose"
            onClick={() => setMetricKind("harvest")}
          />
        </div>

        {/* Zones */}
        <div className="mb-10">
          <ZonesTile
            zones={zones}
            orders={orders}
            now={now}
            onOpenDetail={() => setZonesOpen(true)}
            onManageZones={() => setManageZonesOpen(true)}
          />
        </div>

        {/* Orders */}
        <section className="bg-white rounded-2xl border border-[#E5E5E0] shadow-[0_2px_8px_rgba(0,0,0,0.02)] overflow-hidden">
          <div className="px-6 py-5 border-b border-[#E5E5E0] flex items-center justify-between">
            <div>
              <div className="font-display text-lg font-medium text-stone-900">
                Active Orders
              </div>
              <div className="text-xs text-stone-500 mt-0.5">
                {orders.length > ROW_LIMIT
                  ? `Showing ${ROW_LIMIT} of ${orders.length} · tap a customer or the Active Orders card for all`
                  : `Tap a customer to see full growth details · ${orders.length} order${orders.length === 1 ? "" : "s"}`}
              </div>
            </div>
            {orders.length > ROW_LIMIT && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setMetricKind("active")}
                data-testid="view-all-orders-btn"
                className="text-xs text-emerald-700 hover:text-emerald-800 hover:bg-emerald-50"
              >
                View all {orders.length} →
              </Button>
            )}
          </div>

          {loading ? (
            <div className="p-10 text-center text-stone-500 text-sm">Loading…</div>
          ) : orders.length === 0 ? (
            <div className="p-12 text-center" data-testid="empty-state">
              <div className="inline-flex p-3 rounded-2xl bg-emerald-50 text-emerald-600 mb-4">
                <Package size={20} />
              </div>
              <div className="font-display text-lg text-stone-900 mb-1">No active orders</div>
              <div className="text-sm text-stone-500 mb-5">
                Start tracking by creating your first customer order.
              </div>
              <Button
                onClick={() => {
                  setEditingOrder(null);
                  setFormOpen(true);
                }}
                data-testid="empty-add-order-btn"
                className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2"
              >
                <Plus size={16} /> Add New Order
              </Button>
            </div>
          ) : (
            <ul className="divide-y divide-[#EFEFEC]">
              {computed.slice(0, ROW_LIMIT).map(({ order, calc }) => (
                <OrderRow
                  key={order.id}
                  order={order}
                  calc={calc}
                  onOpen={() => openDetails(order)}
                />
              ))}
            </ul>
          )}
        </section>
      </main>

      <SettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} />
      <ChangePasswordDialog open={changePwOpen} onOpenChange={setChangePwOpen} />
      <OrderFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        order={editingOrder}
        onSaved={fetchOrders}
      />
      <OrderDetailsDialog
        open={!!detailsOrder}
        onOpenChange={(o) => !o && setDetailsOrder(null)}
        order={detailsOrder}
        onEdit={openEdit}
        onDeliver={handleDeliver}
        onDelete={(o) => setDeleteTarget(o)}
      />
      <MetricDetailDialog
        kind={metricKind}
        open={!!metricKind}
        onOpenChange={(o) => !o && setMetricKind(null)}
        orders={orders}
        roundTrays={roundTrays}
        onRowClick={(o) => setDetailsOrder(o)}
      />
      <ZonesDetailDialog
        zones={zones}
        orders={orders}
        now={now}
        open={zonesOpen}
        onOpenChange={setZonesOpen}
        onRowClick={(o) => setDetailsOrder(o)}
      />
      <ManageZonesDialog
        open={manageZonesOpen}
        onOpenChange={setManageZonesOpen}
      />
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="delete-order-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete order?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the order for{" "}
              <span className="font-medium text-stone-900">{deleteTarget?.customer_name}</span>.
              This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              data-testid="delete-confirm-btn"
              className="bg-rose-600 hover:bg-rose-700 text-white"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function OrderRow({ order, calc, onOpen }) {
  const grain = grainTypeMeta(order.grain_type);
  const startedLabel = new Date(order.start_datetime).toLocaleString(undefined, {
    month: "short", day: "numeric", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
  const dayNum = calc.stage.day;

  return (
    <li>
      <button
        type="button"
        onClick={onOpen}
        data-testid={`order-row-${order.id}`}
        className="w-full text-left group px-6 py-5 flex items-center gap-4 hover:bg-emerald-50/40 transition-colors focus:outline-none focus:bg-emerald-50/60"
      >
        {/* Vibrant day indicator */}
        <div
          className={`hidden sm:flex shrink-0 w-12 h-12 rounded-2xl items-center justify-center font-display font-medium ${
            calc.overdue
              ? "bg-rose-100 text-rose-700"
              : "bg-emerald-100 text-emerald-700"
          }`}
          data-testid="row-day-pill"
        >
          <div className="text-[9px] uppercase tracking-[0.15em] leading-none">Day</div>
          <div className="text-base leading-none ml-0.5">{dayNum}</div>
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div
              className="font-display text-base sm:text-lg font-medium text-stone-900 group-hover:text-emerald-700 transition-colors"
              data-testid="order-customer"
            >
              {order.customer_name}
            </div>
            <span
              className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide border ${grain.chip}`}
              data-testid="row-grain-chip"
            >
              <span className={`w-1.5 h-1.5 rounded-full ${grain.dot}`} />
              {grain.label}
            </span>
            <span
              className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide border ${calc.phase.badge}`}
              data-testid="row-phase-chip"
            >
              {calc.phase.short}
            </span>
            {calc.overdue && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide bg-rose-600 text-white">
                Overdue
              </span>
            )}
          </div>
          <div className="text-xs text-stone-500 mt-1 font-mono">{startedLabel}</div>
        </div>

        <div className="hidden sm:flex flex-col items-end mr-2">
          <div className="text-[10px] uppercase tracking-[0.15em] text-stone-500 font-semibold">
            Order
          </div>
          <div className="font-display text-lg font-medium text-stone-900 tabular-nums" data-testid="row-order-weight">
            {formatNum(order.order_quantity_kg, 2)}
            <span className="text-sm text-stone-500 ml-1">kg</span>
          </div>
        </div>

        <ChevronRight
          size={18}
          className="shrink-0 text-stone-400 group-hover:text-emerald-600 group-hover:translate-x-0.5 transition-all"
        />
      </button>
    </li>
  );
}
